import { useEffect, useState } from "react";
import { Button, Divider } from "@nextui-org/react";
import logo from "@/assets/images/logo.svg";
import { loginRequest, msalInstance, msalReady } from "@/lib/msal-config";
import { useRouteTracker } from "@/hooks/useRouteTracker";

// ---------------------- Microsoft Logo

const MicrosoftLogo = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 21 21"
  >
    <rect x="1" y="1" width="9" height="9" fill="#f25022" />
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
    <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
    <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
  </svg>
);

// ---------------------- Component

const LoginPage = () => {
  // --------- Variables / Hooks

  useRouteTracker();

  // Remove the loading overlay so the login button is visible
  useEffect(() => {
    document.getElementById("app-loading-overlay")?.remove();
  }, []);

  const [isRedirecting, setIsRedirecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // --------- Helpers

  const handleLogin = async () => {
    try {
      setIsRedirecting(true);
      setError(null);
      await msalReady;
      await msalInstance.loginRedirect(loginRequest);
    } catch (err) {
      setIsRedirecting(false);
      setError(err instanceof Error ? err.message : "Failed to initiate sign-in");
    }
  };

  // --------- JSX

  return (
    <main className="flex min-h-dvh w-screen flex-col items-center justify-center bg-gray-100 p-6 dark:bg-gray-900 md:p-10">
      <div className="flex w-full max-w-sm flex-col items-center gap-8 rounded-xl border border-gray-200 bg-gray-100 px-8 py-10 dark:border-gray-700 dark:bg-gray-900">
        {/*******************************************/}
        {/* Logo */}
        {/*******************************************/}

        <img
          alt="Field Service Logo"
          src={logo}
          className="h-10 w-auto flex-shrink-0"
        />

        {/*******************************************/}
        {/* Divider */}
        {/*******************************************/}

        <Divider />

        {/*******************************************/}
        {/* Sign In Button */}
        {/*******************************************/}

        <Button
          isDisabled={isRedirecting}
          isLoading={isRedirecting}
          radius="sm"
          className="flex w-full items-center justify-center gap-2.5 bg-[#0078d4] py-5 text-xs text-white hover:bg-[#106ebe]"
          onPress={handleLogin}
          startContent={!isRedirecting && <MicrosoftLogo />}
        >
          {isRedirecting ? "Redirecting to Microsoft..." : "Log in with Microsoft"}
        </Button>

        {/*******************************************/}
        {/* Error Message Area */}
        {/*******************************************/}

        {error && (
          <p className="text-center text-sm text-red-500">{error}</p>
        )}
      </div>
    </main>
  );
};

export default LoginPage;
